import "./locationDetailsScreen.scss";
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../shared/components/tabs/tabsComponent";
import LocationAdminDetailsViewEditComponent
    from "./location-admin-details-view-edit/locationAdminDetailsViewEditComponent";
import LocationDetailsViewEditComponent from "./location-details-view-edit/locationDetailsViewEditComponent";
import LocationPhysicianDetailsViewEditComponent
    from "./location-physician-details-view-edit/locationPhysicianDetailsViewEditComponent";
import LocationPrimaryContactDetailsViewEditComponent
    from "./location-primary-contact-details-view-edit/locationPrimaryContactDetailsViewEditComponent";
import LocationCriticalDetailsViewEditComponent
    from "./location-critical-details-view-edit/locationCriticalDetailsViewEditComponent";
import LocationBillingDetailsViewEditComponent
    from "./location-billing-details-view-edit/locationBillingDetailsViewEditComponent";
import LocationAccountPreferencesDetailsViewEditComponent
    from "./location-account-preferences-details-view-edit/locationAccountPreferencesDetailsViewEditComponent";
import LocationBloodDrawInformationDetailsViewEditComponent
    from "./location-blood-draw-information-details-view-edit/locationBloodDrawInformationDetailsViewEditComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {ImageConfig} from "../../../constants";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import LocationTestAndPanelDetailsViewEditComponent
    from "./location-test-and-panel-details-view-edit/LocationTestAndPanelDetailsViewEditComponent";
import LocationUserViewEditComponent from "./location-user-view-edit/LocationUserViewEditComponent";

interface LocationDetailsScreenProps {

}

const LOCATION_DETAILS = 'location_details';
const PRIMARY_CONTACT = 'primary_contact';
const PROVIDER_OR_PHYSICIAN = 'Provider_or_Physician';
const TEST_AND_PANELS_TAB = 'test_and_panels';
const CRITICAL_DETAILS = "critical_details";
const BILLING_DETAILS = "billing_details";
const ACCOUNT_PREFERENCES = "account_preferences";
const BLOOD_DRAW_INFORMATION = "blood_draw_information";
const ADMIN_DETAILS = "admin_details";
const USER_DETAILS = 'user_details';

const LocationDetailsScreen = (props: LocationDetailsScreenProps) => {
    const [activeTab, setActiveTab] = useState<"admin_details" | "location_details" | "primary_contact" | "provider_or_physician" | "test_and_panels" | "user_details">(ADMIN_DETAILS);
    const navigate = useNavigate();
    const params = useParams<{ locationId: string | undefined }>();
    const {locationId} = params;
    const [searchParams, setSearchParams] = useSearchParams();
    const [locationDetails, setLocationDetails] = useState<any>(undefined);
    const [isLocationDetailsLoading, setIsLocationDetailsLoading] = useState<boolean>(false);
    const [isLocationDetailsLoaded, setIsLocationDetailsLoaded] = useState<boolean>(false);
    const [isLocationDetailsLoadingFailed, setIsLocationDetailsLoadingFailed] = useState<boolean>(false);
    const [locationDetailsMode, setLocationDetailsMode] = useState<"view" | "edit">("view");
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [canEdit, setCanEdit] = useState<boolean>(false);
    const dispatch = useDispatch();

    useEffect(() => {
        setCanEdit(commonService.checkAccess('Location',
            'locationEdit', currentUser?.userAccessModules))
        setActiveTab(searchParams.get("currentTab") as typeof activeTab || ADMIN_DETAILS)
    }, [currentUser, searchParams]);

    useEffect(() => {
        if (locationId) {
            dispatch(setCurrentNavParams("Back", "Location", CommonService._routeConfig.LocationDetailsTestOrderRoute(locationId)));
            dispatch(setSideMenuView("compact"));
        }
    }, [locationId, dispatch, navigate]);

    const getLocationDetails = useCallback(() => {
        const payload = {};
        let locationDetails: any = {};
        setIsLocationDetailsLoading(true);
        setIsLocationDetailsLoaded(false);
        setIsLocationDetailsLoadingFailed(false);
        CommonService._location.getLocationDetails(locationId, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    locationDetails = response.data;
                }
                setLocationDetails(locationDetails);
                setIsLocationDetailsLoading(false);
                setIsLocationDetailsLoaded(true);
                setIsLocationDetailsLoadingFailed(false);
            })
            .catch((error: any) => {
                setLocationDetails(locationDetails);
                setIsLocationDetailsLoading(false);
                setIsLocationDetailsLoaded(false);
                setIsLocationDetailsLoadingFailed(true);
            });
    }, [locationId]);

    const handleAdminDetailsUpdate = useCallback((locationAdminDetails: any) => {
        setLocationDetails((prev: any) => {
            return {
                ...prev,
                ...locationAdminDetails
            }
        });
    }, []);

    const handleLocationBasicInformationDetailsUpdate = useCallback((locationInformation: any) => {
        setLocationDetails(() => {
            return {
                ...locationInformation
            }
        })
    }, []);

    const handleLocationPrimaryContactDetailsUpdate = useCallback((primaryContactDetails: any) => {
        setLocationDetails((prev: any) => {
            return {
                ...prev,
                ...primaryContactDetails
            }
        })
    }, []);

    // const handleLocationPanelInformationDetailsUpdate = useCallback((panels: any[]) => {
    //     setLocationDetails(() => {
    //         return {
    //             ...locationDetails,
    //             panels: panels,
    //         }
    //     })
    // }, [locationDetails]);

    const handleLocationCriticalDetailsUpdate = useCallback((criticalDetails: any) => {
        setLocationDetails(() => {
            return {
                ...locationDetails,
                criticalDetails: criticalDetails,
            }
        })
    }, [locationDetails]);

    const handleLocationBillingDetailsUpdate = useCallback((billingDetails: any) => {
        setLocationDetails(() => {
            return {
                ...locationDetails,
                billingDetails: billingDetails,
            }
        })
    }, [locationDetails]);

    const handleLocationAccountPreferencesDetailsUpdate = useCallback((accountPreferences: any) => {
        setLocationDetails(() => {
            return {
                ...locationDetails,
                accountPreferences: accountPreferences,
            }
        })
    }, [locationDetails]);

    const handleLocationBloodDrawInformationDetailsUpdate = useCallback((bloodDrawInformation: any) => {
        setLocationDetails(() => {
            return {
                ...locationDetails,
                bloodDrawInformation: bloodDrawInformation,
            }
        })
    }, [locationDetails]);


    useEffect(() => {
        if (locationId) {
            getLocationDetails();
        }
    }, [locationId, getLocationDetails]);

    const handleActiveTabChange = useCallback((value: any) => {
        if (locationDetailsMode === 'edit') {
            CommonService.openConfirmationDialog(
                {
                    confirmationTitle: 'Please confirm!',
                    confirmationSubTitle: 'Are you sure you want to discard the recent changes?',
                    disableEscKeyClose: false,
                    disableBackdropClose: true,
                    imagePath: `${ImageConfig.ConfirmationImage}`,
                    direction: "up",
                    yes: {
                        text: "Discard",
                        color: "primary"
                    },
                    no: {
                        text: "Cancel",
                        color: "primary"
                    }
                }).then(() => {
                setActiveTab(value);
            }).catch(() => {

            })
        } else {
            setActiveTab(value);
        }
        searchParams.set("currentTab", value);
        setSearchParams(searchParams);
    }, [locationDetailsMode, searchParams, setSearchParams])

    return (
        <div className={'location-details-component details-screen screen'}>
            {
                isLocationDetailsLoading && <div className="h-v-center">
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
            {
                isLocationDetailsLoadingFailed && <>
                    <StatusCardComponent title={"Location details failed to load"}/>
                </>
            }
            {
                isLocationDetailsLoaded && <>
                    <div className={'details-module-title'}>Location Details</div>
                    <div className="basic-details-wrapper">
                        <div className="details-screen-header-status-section">
                            <div className="details-screen-header">
                                <HeadingComponent
                                    title={`${CommonService.capitalizeFirstLetterOfEachWord(locationDetails?.name)}`}
                                    statusLabel={locationDetails?.status}
                                />
                            </div>
                        </div>

                        <div className="details-screen-sub-header">
                            <DetailsDataKeyValueComponent
                                title={"Location Type:"}
                                isHorizontal={true}> {locationDetails?.type} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Added by:"}
                                isHorizontal={true}> {locationDetails?.createdByDetails?.firstName + " " + locationDetails?.createdByDetails?.lastName} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Created on:"}
                                isHorizontal={true}>
                                <>
                                    {commonService.formatDate(locationDetails?.createdAt)}
                                    &nbsp;|&nbsp;
                                    {commonService.formatTime(locationDetails?.createdAt)}
                                </>

                            </DetailsDataKeyValueComponent>
                        </div>

                    </div>
                    <div className="clear-fix"/>
                    <div className="details-screen-body">
                        <TabsWrapperComponent>
                            <TabsComponent
                                value={activeTab}
                                allowScrollButtonsMobile={true}
                                onUpdate={(e: any, v: any) => {
                                    handleActiveTabChange(v);
                                }}
                                scrollButtons={true}
                                variant={"scrollable"}
                            >
                                <TabComponent label="ADMIN DETAILS" value={ADMIN_DETAILS}/>
                                <TabComponent label={"USER DETAILS"} value={USER_DETAILS}/>
                                <TabComponent label="LOCATION DETAILS" value={LOCATION_DETAILS}/>
                                <TabComponent label="PRIMARY CONTACT" value={PRIMARY_CONTACT}/>
                                <TabComponent label="PROVIDER/PHYSICIAN" value={PROVIDER_OR_PHYSICIAN}/>
                                <TabComponent label="TESTS AND PANELS" value={TEST_AND_PANELS_TAB}/>
                                <TabComponent label="CRITICAL DETAILS" value={CRITICAL_DETAILS}/>
                                <TabComponent label="BILLING DETAILS" value={BILLING_DETAILS}/>
                                <TabComponent label="ACCOUNT PREFERENCES" value={ACCOUNT_PREFERENCES}/>
                                <TabComponent label="BLOOD DRAW INFORMATION" value={BLOOD_DRAW_INFORMATION}/>
                            </TabsComponent>
                            <TabContentComponent selectedTab={activeTab} value={ADMIN_DETAILS}>
                                <LocationAdminDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    onUpdateAdminDetails={handleAdminDetailsUpdate}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={USER_DETAILS}>
                                <LocationUserViewEditComponent
                                    locationDetails={locationDetails}
                                    onUpdateAdminDetails={setLocationDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={LOCATION_DETAILS}>
                                <LocationDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    canEdit={canEdit}
                                    onUpdateLocationInformation={handleLocationBasicInformationDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={PRIMARY_CONTACT}>
                                <LocationPrimaryContactDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    canEdit={canEdit}
                                    onUpdatePrimaryContactDetails={handleLocationPrimaryContactDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={PROVIDER_OR_PHYSICIAN}>
                                <LocationPhysicianDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={TEST_AND_PANELS_TAB}>
                                <LocationTestAndPanelDetailsViewEditComponent
                                    locationId={locationId}
                                    canEdit={canEdit}
                                    locationDetails={locationDetails}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={CRITICAL_DETAILS}>
                                <LocationCriticalDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    canEdit={canEdit}
                                    onUpdateCriticalDetails={handleLocationCriticalDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={BILLING_DETAILS}>
                                <LocationBillingDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    canEdit={canEdit}
                                    onUpdateBillingDetails={handleLocationBillingDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={ACCOUNT_PREFERENCES}>
                                <LocationAccountPreferencesDetailsViewEditComponent
                                    locationDetails={locationDetails}
                                    canEdit={canEdit}
                                    onUpdateAccountPreferencesDetails={handleLocationAccountPreferencesDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setLocationDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent><TabContentComponent selectedTab={activeTab}
                                                                       value={BLOOD_DRAW_INFORMATION}>
                            <LocationBloodDrawInformationDetailsViewEditComponent
                                locationDetails={locationDetails}
                                canEdit={canEdit}
                                onUpdateBloodDrawInformationDetails={handleLocationBloodDrawInformationDetailsUpdate}
                                onModeChange={(mode: any) => {
                                    setLocationDetailsMode(mode);
                                }
                                }
                            />
                        </TabContentComponent>
                        </TabsWrapperComponent>
                    </div>
                </>
            }
        </div>
    );

};

export default LocationDetailsScreen;
